/**
 * Scoping a revenue document's Contact picker to the company on that document,
 * and seeding that company's designated primary contact.
 *
 * ONE module for THREE forms — `DealFormModal`, `QuoteFormModal` and
 * `InvoiceFormModal` — for the same reason `document-naming.ts` is one module
 * for a deal's name and a quote's subject: three copies would agree the day they
 * were written and drift on the first edit, and every sentence here is copy that
 * nothing typechecks.
 *
 * Owner's instruction, 2026-08-29: *"In the deals / quotes / invoices contact
 * selection should be on the basis of the company added in them from picker,
 * only the contacts associated from those company should come and the primary
 * contact can be the one autoselected"*.
 *
 * ## ⚠️ THE SERVER DOES NOT CHECK THIS. The client is the only guard
 *
 * Measured in `emerge-backend-app` on 2026-08-30, from the source rather than
 * from a response: all three services validate a contact through
 * `FOREIGN_KEYS` → `repository.contactsExist`, which asks whether the row
 * EXISTS and nothing else (`deals.service.ts:58`, `quotes.service.ts:84`,
 * `invoices.service.ts:115`). There is no belongs-to-company check on any of the
 * three.
 *
 * ⚠️ **So a contact belonging to a different company saves with a `200`.** It is
 * not a 422 to be handled, not a refusal to be worded — it is silently accepted,
 * and the document then names a person at another account. That is the opposite
 * of a deliverable, whose `pointOfContactId` IS checked against the post-merge
 * company (`deliverables.service.ts:61`, `contactBelongsToCompany`) and comes
 * back 422 when the pair disagrees.
 *
 * Two consequences, both easy to get backwards:
 *
 * - **`{companyId, contactId}` do NOT have to travel together in one PATCH.**
 *   The pair rules this codebase repeats — `{cycleId, intakeId}`,
 *   `{companyId, pointOfContactId}`, `{issueDate, validTill}`,
 *   `{currency, currencyRate}`, `{metric, baseCurrency}` — all exist because a
 *   server judges those pairs on the POST-MERGE record. This one is not that
 *   shape. Sending `companyId` alone is a perfectly ordinary write.
 * - **Which makes the client-side reset MORE important, not less.** Where a
 *   server refuses, a forgotten reset is a visible 422 somebody reports. Here a
 *   forgotten reset is a stored, plausible, wrong contact that nothing will ever
 *   flag.
 *
 * ## Where the picker gets its options
 *
 * `GET /contacts?companyId=<id>` — a real server-side filter
 * (`listContactsQuerySchema.companyId`, `contacts.schema.ts:223`), so the picker
 * is SCOPED rather than filtered locally: a contact at another account is never
 * offered, at any page size.
 *
 * ⚠️ **A SINGLE id. `?companyId=15,16` is a 422** — the comma-separated form is
 * companies-only, and `contacts.schema.ts` says so in place, next to a
 * `z.coerce.number()` that is deliberately not `csvIds()`.
 *
 * The precedent this follows throughout is the intake picker in
 * `ProjectFormModal`: scoped to the selected cycle, EMPTY when no cycle is
 * chosen, with a hint saying why, and cleared when the parent moves.
 */

/**
 * ⚠️ **What the picker does when NO company is chosen: it offers NOTHING**, and
 * says so.
 *
 * The alternative — fall back to the unscoped directory of every contact — is
 * the behaviour the owner asked to remove, so it cannot be the fallback for the
 * case that reaches it most often. It is also the more dangerous half: an
 * unscoped list is the only way to pick a contact that the company will later
 * contradict, and nothing server-side would refuse it.
 *
 * Matches `intakeHint`'s `selectedCycleId === null` branch exactly. Note the two
 * fields differ in whether the parent is REQUIRED — a project must name a cycle,
 * a quote need not name a company — which changes how often this sentence is
 * read, not what it should say.
 */
export const CONTACT_NEEDS_COMPANY =
  'Choose a company first — the contact list is that company’s own.';

/**
 * ⚠️ **A failed fetch must never read as "this company has no contacts."**
 *
 * Both render an empty list, and the wrong one of the two sends somebody to add
 * a contact that already exists. The second sentence is the actionable half:
 * with the list unread, a contact chosen now is one SEED EMERGE could not check
 * against the company — and since the API accepts the mismatch, that check is
 * the only one there is.
 */
export const CONTACT_SCOPE_UNREADABLE =
  'This company’s contacts could not be loaded. Try again shortly — a contact chosen now could not be checked against the company.';

/**
 * The genuinely empty case, which is ordinary rather than a fault: a company
 * created today has no contacts against it yet.
 *
 * ⚠️ **CORRECTED 2026-08-31, and the superseded half is kept because it argues
 * for a decision that has been reversed.** *SUPERSEDED: "Says where the fix is,
 * because it is not on this form — contacts are created on `/contacts` or from
 * the company record, and there is no inline "+ Add" here (unlike the deal
 * pickers' `PickerField`, whose masters are picklists a salesperson may extend;
 * a contact is a person with an email, an owner and a lead source, and inventing
 * one from a two-field row is how a directory fills with half-records)."*
 *
 * There **is** an inline "+ Add a new contact…" row now, on the owner's
 * instruction. The objection it was withheld for is answered rather than
 * overruled: the row opens the **whole** `ContactFormModal` with the company
 * locked, not a two-field row of its own — so nothing here can create the
 * half-record that paragraph was protecting the directory from.
 *
 * The sentence itself is unchanged and is still the right one: it is read when
 * the account genuinely has nobody on it, and "add one on the company record"
 * remains true and is the place a person ends up if they close this form.
 */
export const CONTACT_SCOPE_EMPTY =
  'No contacts are recorded against this company yet. Add one on the company record first.';

/**
 * The inline create row's label, in the wording `PickerField` and
 * `MultiCombobox`'s `addNew` already use — an action, with the noun in it.
 */
export const CONTACT_ADD_NEW_LABEL = '+ Add a new contact…';

/**
 * Why the row is withheld when this company's contacts could not be read.
 *
 * ⚠️ **Said out loud, never merely absent.** A picker offering "add a new
 * contact" over a list that never loaded invites somebody to create a person the
 * account already has — and unlike the deal pickers' masters, `d_contacts` has
 * no UNIQUE name, so the duplicate is accepted with a `201` and there is nothing
 * to merge it back with afterwards. Suppression is the right answer and is only
 * safe because it is announced: an affordance that vanishes reads as a control
 * that broke, and sends the reader hunting.
 *
 * Same rule, and very nearly the same sentence, as `PickerField`'s `failed`
 * warning. It is a why-this-is-unavailable sentence, so the no-helper-text rule
 * does not reach it.
 */
export const CONTACT_ADD_NEW_UNAVAILABLE =
  'Adding a contact is off until this company’s contacts load — one added now could duplicate somebody already on the account.';

/**
 * Whether the Contact picker may offer its inline "+ Add a new contact…" row,
 * and what to say when it may not.
 *
 * ONE decision point for the three forms, for the same reason every other
 * sentence in this module is: three copies would agree the day they were written
 * and drift on the first correction.
 *
 * The order of the branches is the order the reasons OUTRANK each other, and
 * only one sentence is ever returned — the field renders `contactScopeHint`
 * beside it, and two sentences saying "no company" would be one drop of the
 * `a ?? b` rule away from each other:
 *
 * 1. **No company.** ⚠️ Withheld with **no reason of its own**: a contact must
 *    belong to the document's company (`ContactFormModal`'s `lockedCompany` is
 *    the whole point of this flow), and there is no company to lock it to. The
 *    hint already reads `CONTACT_NEEDS_COMPANY`, which is both the reason and
 *    the way out, so repeating it here would print it twice.
 * 2. **`company` unnamed.** The id is known and the NAME is not, which is the
 *    one state `lockedCompany` cannot express — it takes a name because the
 *    contact form shows the account read-only rather than looking it up. Same
 *    silence, same sentence in the hint, because it is reachable only while the
 *    company list is still landing.
 * 3. **Cannot create contacts.** Withheld silently, exactly as
 *    `ProjectFormModal` withholds its "+ Add a new city…" row from a
 *    non-admin: the reader never had this affordance, so there is nothing to
 *    explain the absence of. (Unlike a control that is RENDERED disabled, where
 *    `denialReason` is required.)
 * 4. **The list failed.** Withheld and SAID — see `CONTACT_ADD_NEW_UNAVAILABLE`.
 *
 * ⚠️ **`loading` is deliberately NOT a withholding condition.** `Combobox`
 * renders the row while a list is in flight so the keyboard offset is a constant
 * for as long as the popover is open, and `scope.loading` here goes true on
 * every debounced keystroke of the search box — a row that blinked in and out
 * while somebody typed would be the worst of both. The duplicate risk this
 * guards against is the list that did not come back, which is `failed`.
 */
export type ContactAddNewOffer =
  | { offered: true; company: { id: number; name: string } }
  | { offered: false; reason: string | null };

export function contactAddNewOffer(input: {
  scope: ContactScope;
  /** `ability.canCreate('contacts')`. Never a role check, never `/permissions/me`. */
  canCreate: boolean;
  /** The document's company, with its name, or `null` when it names none. */
  company: { id: number; name: string } | null;
}): ContactAddNewOffer {
  const { scope, canCreate, company } = input;
  if (scope.companyId === null || company === null) return { offered: false, reason: null };
  if (!canCreate) return { offered: false, reason: null };
  if (scope.failed) return { offered: false, reason: CONTACT_ADD_NEW_UNAVAILABLE };
  return { offered: true, company };
}

/**
 * Said when the company MOVED and took the contact with it.
 *
 * ⚠️ Without it, a value the user can see disappearing looks like the form
 * losing their work. Same job as `intakeHint`'s `cycleChanged` branch, and the
 * same reason it is a sentence rather than a silent clear.
 */
export const CONTACT_CLEARED_BY_COMPANY = 'The company changed, so the contact was cleared.';

/**
 * The option hint on a stored contact that is NOT among the company's own.
 *
 * ⚠️ **The option is offered either way; only this REASON is gated.** A stored
 * contact must stay selectable — an edit whose contact is dropped from the list
 * renders as "Unassigned" over a record that holds one, and the next save writes
 * that emptiness back. It is the retired-picker-option trap this codebase has
 * been bitten by repeatedly, and the pin is the same answer
 * (`withStoredOption`, `selectablePickerOptions`, `productOptions`' retired
 * pin).
 *
 * Reachable without anything being broken: a document raised before this change
 * shipped, or one whose company was re-parented on the company record rather
 * than here.
 */
export const CONTACT_NOT_ON_COMPANY = 'Not one of this company’s contacts';

/**
 * What the Contact field knows about its own option list, in the one shape all
 * three forms report it.
 */
export interface ContactScope {
  /** The company the list is scoped to; `null` when the form names none. */
  companyId: number | null;
  /** True while the list for `companyId` has not arrived yet. */
  loading: boolean;
  /** True when the request for `companyId` came back an error. */
  failed: boolean;
  /** How many contacts the scoped list returned. */
  count: number;
  /**
   * True when a search term is applied. ⚠️ Load-bearing: `?search=` is
   * server-side, so an empty list under a term means "nothing matched", NOT
   * "this company has no contacts" — and the second sentence, said over the
   * first, is a flat lie about the account.
   */
  searching: boolean;
  /** True when the user changed the company and the contact was cleared for it. */
  cleared: boolean;
}

/**
 * The Contact field's hint — one sentence, resolved in the order a reader needs
 * the facts.
 *
 * `undefined` while the list is loading: the Combobox renders its own loading
 * state, and a second sentence saying the same thing would only flicker.
 *
 * ⚠️ **`cleared` is PREPENDED rather than returned alone**, because it and the
 * state of the new company's list are both true at once and both matter — the
 * case where somebody is most stuck is a company change onto an account with no
 * contacts, where a lone "the contact was cleared" explains the disappearance
 * and not the empty list under it. This is the `a ?? b` rule from `CLAUDE.md`
 * applied to copy: if both sides can be true at once, rendering one is a silent
 * drop.
 */
export function contactScopeHint(scope: ContactScope): string | undefined {
  if (scope.companyId === null) return CONTACT_NEEDS_COMPANY;
  if (scope.loading) return undefined;

  const prefix = scope.cleared ? `${CONTACT_CLEARED_BY_COMPANY} ` : '';
  if (scope.failed) return `${prefix}${CONTACT_SCOPE_UNREADABLE}`;
  if (scope.count === 0 && !scope.searching) return `${prefix}${CONTACT_SCOPE_EMPTY}`;
  return scope.cleared ? CONTACT_CLEARED_BY_COMPANY : undefined;
}

/**
 * Whether the scoped list is a trustworthy answer to "who are this company's
 * contacts" — the gate on the `CONTACT_NOT_ON_COMPANY` label.
 *
 * ⚠️ Withheld while loading, while failed, and while a search term narrows it.
 * All three make every stored contact look absent, and accusing a contact of
 * belonging to another company on the strength of a request that never came back
 * is a confident wrong answer where saying nothing costs nothing. Same gate as
 * `productOptions`' `listIsTheActiveMaster` in `ProjectFormModal`.
 */
export function contactScopeIsAuthoritative(scope: ContactScope): boolean {
  return scope.companyId !== null && !scope.loading && !scope.failed && !scope.searching;
}

/**
 * The company's designated primary contact, as the Contact picker's seed.
 *
 * Returned as `{id, name}` rather than a bare id because **a seeded link needs
 * pinning exactly as a stored one does**: `values.contactId` holding an id
 * selects nothing until an option with that value exists, so an unpinned seed
 * renders "Unassigned" over a form that is carrying the id — and the person
 * either believes nothing was filled in, or clears a field that was never empty.
 * The scoped list will normally contain this person, but not before it lands and
 * not at all if it fails, which is exactly when the pin earns its place.
 *
 * ⚠️ **`null` is an ordinary state, not a fault.** A company that designates
 * nobody seeds nothing and leaves the picker empty; it is never an instruction
 * to CLEAR a contact that is already there. `primary_contact_id` is nullable and
 * migration 098 backfilled it for 130 of the accounts, so the companies it could
 * not match still read `primaryContact: null` today.
 *
 * ⚠️ The primary's own `email` may be null while the object is present — see
 * `CompanyPrimaryContact` — so nothing here may test the email to decide whether
 * a primary exists. The presence of the object is the whole test.
 */
export function primaryContactSeed(company: {
  primaryContact: { id: number; name: string } | null;
}): { id: number; name: string } | null {
  const primary = company.primaryContact;
  return primary ? { id: primary.id, name: primary.name } : null;
}
